import clsx from "clsx";
import { DateFormat, formatDate } from "common";
import { getPaymentReminder } from "common/src/models/PaymentReminder";
import React from "react";
import { PaymentReminderType, Purchase, PurchaseItem, PurchaseItemObject } from "types";
import { CreatePurchaseItemState } from "../../stores/createPurchaseStore";
import { Currency } from "../Common/Currency";

interface BalanceDueProps {
    purchase: Purchase;
    item?: PurchaseItem | CreatePurchaseItemState;
}

export const BalanceDue = ({ purchase, item }: BalanceDueProps) => {
    if (item && item.object === PurchaseItemObject.GIFT) {
        // We do not show balance due for gifts because the balance is actually what they can spend and not what they owe
        return null;
    }

    const invoiceTraveler = getPaymentReminder(purchase, [PaymentReminderType.INVOICE_TRAVELER]);
    let invoiceAmount = 0;
    const balanceDueTraveler = getPaymentReminder(purchase, [
        PaymentReminderType.BALANCE_DUE_TRAVELER,
        PaymentReminderType.SELLER_DEPOSIT,
    ]);

    let remainderAmount = 0;
    // If the invoice has amount, we will display here only if the value is more than balance
    // Above scenario will happen in collect balance modal where the invoiceTraveler status will still be active because of dryRun
    if (invoiceTraveler) {
        // If there is partial invoice, we will show that amount,
        // else purchase balance since it will be invoice for total balance
        invoiceAmount = invoiceTraveler.amount ?? purchase?.balance ?? 0;
        if (invoiceTraveler.amount && invoiceTraveler.amount > purchase.balance) {
            invoiceAmount = 0;
        }

        if (invoiceAmount >= 0) {
            remainderAmount = purchase.balance - invoiceAmount;
        }
    }

    return (
        <div className={clsx("text-2xl font-bold", purchase.balance > 0 ? "text-red" : "text-green")}>
            <div className="flex justify-between">
                <div>{purchase.balance > 0 ? "Balance Due" : "Refund"}</div>
                <Currency>{Math.abs(purchase.balance)}</Currency>
            </div>
            <div className="text-right">
                {purchase.balance > 0 && (
                    <div className="flex w-full flex-col items-end justify-end text-sm font-semibold">
                        {invoiceTraveler && invoiceAmount > 0 && (
                            <div className="space-x-1 " data-testid="balance-due-invoice">
                                <span>Invoice (due {formatDate(invoiceTraveler.dueAt, DateFormat.DATE)})</span>
                                <span className="font-bold">
                                    <Currency>{invoiceAmount}</Currency>
                                </span>
                            </div>
                        )}

                        {balanceDueTraveler && remainderAmount > 0 && (
                            <div className="space-x-1 " data-testid="balance-due-reminder">
                                <span>Total payment (due {formatDate(balanceDueTraveler.dueAt, DateFormat.DATE)})</span>
                                <span className="font-bold">
                                    <Currency>{remainderAmount}</Currency>
                                </span>
                            </div>
                        )}

                        {!invoiceTraveler && balanceDueTraveler && (
                            <div className="space-x-1 " data-testid="balance-due">
                                <span>(due {formatDate(balanceDueTraveler.dueAt, DateFormat.DATE)})</span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
